import { View } from '@play-co/timestep-core/ui';
import uiConfig from 'src/lib/ui/config';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import * as Social from 'src/replicant/social';
import { user } from 'src/StateObserver';
import type { SocialSubFeature } from 'src/replicant/social/social.ruleset';
import {
  FlexBoxImageScaleView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import { Avatar } from './shared/Avatar';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import { assertNever } from 'src/replicant/utils';

type Friend = {
  id: string;
  name: string;
  photo: string;
};

const ui = uiConfig.buruburu.invite.friendTile;

export class FriendTile {
  public readonly view = new FlexBoxImageScaleView(ui.container);
  private icon: Avatar;
  private name: LangBitmapFontTextView;

  constructor(opts: {
    friend: Friend;
    onClick: () => Promise<void>;
    subFeature: SocialSubFeature;
  }) {
    const reward = Social.getters.getInviteReward(user(), opts.subFeature);

    // icon
    this.icon = new Avatar({
      image: opts.friend.photo,
      superview: this.view,
      ...ui.image,
      centerOnOrigin: false,
    });

    getFlexNode(this.icon).updateStyle({
      marginLeft: 12,
      marginRight: 10,
    });

    const content = this.view.addSubview(
      new View({
        height: this.view.style.height,
      }),
    );
    getFlexNode(content).updateStyle({ flexGrow: 1 });

    // name
    this.name = content.addSubview(
      new LangBitmapFontTextView({
        ...ui.name,
        localeText: () => opts.friend.name,
        verticalAlign: 'center',
      }),
    );

    // price
    content.addSubview(
      new LangBitmapFontTextView({
        ...ui.amount,
        localeText: () => `x${parseAmount(reward.value)}`,
      }),
    );

    // currency icon
    const type = reward.type;
    switch (type) {
      case 'gems':
        content.addSubview(new ImageScaleView(ui.currencyGems));
        break;
      case 'energy':
        content.addSubview(new ImageScaleView(ui.currencyEnergy));
        break;
      case 'moves':
        content.addSubview(new ImageScaleView(ui.moves));
        break;
      default:
        assertNever(type);
    }

    // invite cta
    // TODO This cta element should be passed in via composition so that the tile component can be used generically
    const clickTrap = this.view.addSubview(
      new ButtonScaleView({
        onClick: opts.onClick,
        tracking: {
          feature: Feature.INVITE._,
          $subFeature: Feature.INVITE.INVITE_POPUP_MULTI_SELECT,
        },
      }),
    );
    getFlexNode(clickTrap).updateStyle({
      position: 'absolute',
      width: '100%',
      height: '100%',
    });

    this.name.style.width += ui.cta.width - 20;
  }

  /**
   * Change friend for this view
   */
  public setFriend(friend: Friend) {
    this.icon.updateImage(friend.photo);
    this.name.localeText = () => friend.name;
  }
}
